import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TodayScreen, TasksScreen, CalendarScreen, RemindersScreen, AssignScreen, IntegrationsScreen } from "./phone/screens";

const TABS = [
  { id: "today", label: "Today", desc: "One calm view of everything that matters today — events, tasks and reminders, prioritized for you.", Screen: TodayScreen },
  { id: "tasks", label: "Tasks & To-Dos", desc: "Capture anything in seconds. GoDiGo files it under the right list, deadline and priority.", Screen: TasksScreen },
  { id: "calendar", label: "Calendar & Events", desc: "Appointments and events flow straight into your calendar, synced with Google Calendar.", Screen: CalendarScreen },
  { id: "reminders", label: "Reminders & Alarms", desc: "One-off, recurring or location-aware — GoDiGo nudges you exactly when it counts.", Screen: RemindersScreen },
  { id: "assign", label: "Assign Tasks", desc: "Delegate responsibilities to family or teammates and track progress without the chase.", Screen: AssignScreen },
  { id: "integrations", label: "Integrations", desc: "Gmail, Google Calendar and Contacts — connected only with your explicit authorization.", Screen: IntegrationsScreen },
];

export default function Showcase() {
  const [active, setActive] = useState("today");
  const tab = TABS.find((t) => t.id === active);
  const Screen = tab.Screen;

  return (
    <section className="relative overflow-hidden py-20 lg:py-32" data-testid="app-showcase-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="overline-label">Inside the app</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            The GoDiGo experience,<br />screen by screen.
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <div className="flex flex-col gap-2" data-testid="showcase-tabs">
              {TABS.map((t, i) => (
                <motion.button
                  key={t.id}
                  data-testid={`showcase-tab-${t.id}`}
                  onClick={() => setActive(t.id)}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className={`group rounded-2xl border p-5 text-left transition-all duration-300 ${
                    active === t.id
                      ? "border-[#1B4D3E] bg-[#1B4D3E] text-white shadow-lg"
                      : "border-slate-200 bg-white text-slate-800 hover:border-emerald-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-base font-bold">{t.label}</span>
                    <span className={`font-mono-accent text-xs ${active === t.id ? "text-lime-300" : "text-slate-400"}`}>0{i + 1}</span>
                  </div>
                  <AnimatePresence initial={false}>
                    {active === t.id && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden pt-2 text-sm leading-relaxed text-emerald-100"
                      >
                        {t.desc}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="order-1 flex justify-center lg:order-2" data-testid="showcase-phone">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.97 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <Screen />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
